import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from '../utils/safeToast';
import { Plus, ClipboardList, Search, Filter, Loader2, X, Tag } from 'lucide-react';

const API = "/api";

// Mapping von Aufgaben-Kategorie zu Tag-Kategorie
const CATEGORY_MAPPING = {
  eigentuemer: 'eigentuemer',
  reinigung: 'reinigung',
  reparatur: 'handwerker'
};

const TasksPage = () => {
  const [searchParams] = useSearchParams();
  const [tasks, setTasks] = useState([]);
  const [properties, setProperties] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: searchParams.get('status') || 'all',
    priority: 'all',
    category: 'all',
    property_id: searchParams.get('property') || 'all',
    tag_id: 'all'
  });
  const [formData, setFormData] = useState({
    property_id: '',
    title: '',
    description: '',
    category: 'reparatur',
    priority: 'normal'
  });
  const [submitting, setSubmitting] = useState(false);
  
  // Tag-Generierung States
  const [categoryTags, setCategoryTags] = useState([]);
  const [selectedTextTags, setSelectedTextTags] = useState([]);
  const [generatingText, setGeneratingText] = useState(false);
  const [rawNotes, setRawNotes] = useState(''); // Stichsätze für Textgenerierung
  const [confirmOverwriteOpen, setConfirmOverwriteOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  // Lade Tags wenn der Dialog geöffnet wird oder sich die Kategorie ändert
  useEffect(() => {
    if (dialogOpen) {
      loadCategoryTags(formData.category);
    }
  }, [dialogOpen, formData.category]);

  const fetchData = async () => {
    try {
      const [tasksRes, propertiesRes, tagsRes] = await Promise.all([
        axios.get(`${API}/tasks`),
        axios.get(`${API}/properties`),
        axios.get(`${API}/tags`)
      ]);
      setTasks(tasksRes.data);
      setProperties(propertiesRes.data);
      setAvailableTags(tagsRes.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Aufgaben');
    } finally {
      setLoading(false);
    }
  };

  // Lade Tags für eine bestimmte Kategorie (für Textgenerierung)
  const loadCategoryTags = async (category) => {
    const tagCategory = CATEGORY_MAPPING[category] || category;
    try {
      const res = await axios.get(`${API}/tags`, { params: { category: tagCategory } });
      setCategoryTags(res.data.filter(t => t.is_active !== false));
    } catch (error) {
      console.error('Fehler beim Laden der Kategorie-Tags:', error);
      setCategoryTags([]);
    }
  };

  // Tag-Auswahl für Textgenerierung umschalten
  const handleTextTagToggle = (tagId) => {
    setSelectedTextTags(prev => 
      prev.includes(tagId) 
        ? prev.filter(t => t !== tagId) 
        : [...prev, tagId]
    );
  };

  // Stichsätze in vollständige Sätze umwandeln
  const transformNotesToSentences = (notes) => {
    if (!notes || !notes.trim()) return [];
    
    return notes
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0)
      .map(line => {
        // Entferne führende Aufzählungszeichen
        let cleaned = line.replace(/^[-•*]\s*/, '').trim();
        
        // Ersten Buchstaben großschreiben
        if (cleaned.length > 0) {
          cleaned = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
        }
        
        // Punkt am Ende hinzufügen, falls nicht vorhanden
        if (cleaned.length > 0 && !cleaned.match(/[.!?]$/)) {
          cleaned += '.';
        }
        
        return cleaned;
      })
      .filter(line => line.length > 1);
  };

  // Regelbasierte Textgenerierung (ohne KI)
  const generateRuleBasedText = async () => {
    // Für Eigentümer-Kategorie: Lade Eigentümer-Daten vom Objekt
    let ownerName = '';
    let ownerSalutation = '';
    
    if (formData.category === 'eigentuemer' && formData.property_id) {
      try {
        const propRes = await axios.get(`${API}/properties/${formData.property_id}`);
        if (propRes.data.owner_id) {
          const ownerRes = await axios.get(`${API}/owners`);
          const owner = ownerRes.data.find(o => o.id === propRes.data.owner_id);
          if (owner) {
            ownerName = owner.name;
            ownerSalutation = owner.salutation || '';
          }
        }
      } catch (error) {
        console.error('Fehler beim Laden des Eigentümers:', error);
      }
    }
    
    // Anrede generieren
    let greeting = '';
    if (ownerName) {
      const lastName = ownerName.split(' ').pop() || ownerName;
      if (ownerSalutation === 'Frau' || ownerSalutation === 'Herr') {
        greeting = `Sehr geehrte${ownerSalutation === 'Frau' ? '' : 'r'} ${ownerSalutation} ${lastName},`;
      } else {
        greeting = `Moin ${ownerName},`;
      }
    } else {
      greeting = 'Sehr geehrte Damen und Herren,';
    }
    
    // Tag-Namen sammeln
    const selectedTagNames = selectedTextTags
      .map(tagId => categoryTags.find(t => t.id === tagId))
      .filter(Boolean)
      .map(tag => tag.name);
    
    // Notizen transformieren
    const sentences = transformNotesToSentences(rawNotes);
    
    // Titel generieren
    let title = '';
    if (selectedTagNames.length > 0) {
      title = selectedTagNames.slice(0, 3).join(', ');
      if (selectedTagNames.length > 3) {
        title += ' u.a.';
      }
    } else if (sentences.length > 0) {
      // Ersten Stichsatz als Titel verwenden
      title = sentences[0].replace(/\.$/, '');
      if (title.length > 50) {
        title = title.substring(0, 47) + '...';
      }
    } else {
      title = 'Hinweise zum Objekt';
    }
    
    // Text zusammenbauen
    let text = greeting + '\n\n';
    text += 'bei einer Begehung des Objekts wurden folgende Punkte festgestellt:\n\n';
    
    // Betroffene Bereiche (nur wenn Tags vorhanden)
    if (selectedTagNames.length > 0) {
      text += '**Betroffene Bereiche:**\n';
      selectedTagNames.forEach(tagName => {
        text += `• ${tagName}\n`;
      });
      text += '\n';
    }
    
    // Hinweise / Details (nur wenn Notizen vorhanden)
    if (sentences.length > 0) {
      text += '**Hinweise / Details:**\n';
      sentences.forEach(sentence => {
        text += `• ${sentence}\n`;
      });
      text += '\n';
    }
    
    text += 'Bitte geben Sie uns kurz Rückmeldung, wie wir hier weiter vorgehen sollen.\n\n';
    text += 'Vielen Dank und freundliche Grüße\n';
    text += 'GB Sylt GmbH';
    
    return { title, text };
  };

  // Prüfen ob Button aktiviert werden soll
  const canGenerateText = selectedTextTags.length > 0 || (rawNotes && rawNotes.trim().length > 0);

  // Text generieren Handler
  const handleGenerateText = async () => {
    if (!canGenerateText) {
      toast.error('Bitte wählen Sie mindestens einen Tag aus oder geben Sie Notizen ein');
      return;
    }
    
    // Warnung wenn bereits Text vorhanden
    if (formData.description && formData.description.trim().length > 0) {
      setConfirmOverwriteOpen(true);
      return;
    }
    
    executeTextGeneration();
  };

  const executeTextGeneration = async () => {
    setGeneratingText(true);
    setConfirmOverwriteOpen(false);
    
    try {
      const { title, text } = await generateRuleBasedText();
      
      setFormData(prev => ({
        ...prev,
        title: title,
        description: text
      }));
      
      const tagCount = selectedTextTags.length;
      const noteCount = transformNotesToSentences(rawNotes).length;
      
      let message = 'Text generiert';
      if (tagCount > 0 && noteCount > 0) {
        message = `Text aus ${tagCount} Tag(s) und ${noteCount} Notiz(en) generiert`;
      } else if (tagCount > 0) {
        message = `Text aus ${tagCount} Tag(s) generiert`;
      } else if (noteCount > 0) {
        message = `Text aus ${noteCount} Notiz(en) generiert`;
      }
      
      toast.success(message);
    } catch (error) {
      toast.error('Fehler bei der Textgenerierung');
    } finally {
      setGeneratingText(false);
    }
  };

  // Kategorie-Wechsel Handler
  const handleCategoryChange = (value) => {
    setFormData({ ...formData, category: value });
    setSelectedTextTags([]); // Reset ausgewählte Tags
    setRawNotes(''); // Reset Notizen
    // Tags werden automatisch durch useEffect geladen
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.property_id) {
      toast.error('Bitte wählen Sie ein Objekt');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/tasks`, formData);
      toast.success('Aufgabe erfolgreich erstellt');
      setDialogOpen(false);
      setFormData({
        property_id: '',
        title: '',
        description: '',
        category: 'reparatur',
        priority: 'normal'
      });
      setSelectedTextTags([]); // Reset Tags
      setRawNotes(''); // Reset Notizen
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
    } finally {
      setSubmitting(false);
    }
  };

  const clearFilters = () => {
    setFilters({ status: 'all', priority: 'all', category: 'all', property_id: 'all', tag_id: 'all' });
  };

  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filters.status === 'all' || task.status === filters.status;
    const matchesPriority = filters.priority === 'all' || task.priority === filters.priority;
    const matchesCategory = filters.category === 'all' || task.category === filters.category;
    const matchesProperty = filters.property_id === 'all' || task.property_id === filters.property_id;
    
    // Tag Filter: Aufgabe muss den ausgewählten Tag haben
    const matchesTags = filters.tag_id === 'all' || 
      (task.tags || []).includes(filters.tag_id);
    
    return matchesSearch && matchesStatus && matchesPriority && matchesCategory && matchesProperty && matchesTags;
  });

  // Tags alphabetisch sortieren
  const sortedTags = [...availableTags].sort((a, b) => 
    a.name.localeCompare(b.name, 'de')
  );

  const getStatusBadge = (status) => {
    const styles = {
      neu: 'bg-blue-100 text-blue-700',
      in_bearbeitung: 'bg-amber-100 text-amber-700',
      erledigt: 'bg-emerald-100 text-emerald-700'
    };
    const labels = {
      neu: 'Neu',
      in_bearbeitung: 'In Bearbeitung',
      erledigt: 'Erledigt'
    };
    return <Badge className={`${styles[status]} border-0`}>{labels[status]}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    if (priority === 'hoch') {
      return <Badge className="bg-red-100 text-red-700 border-0 font-bold">Hoch</Badge>;
    }
    return <Badge className="bg-stone-100 text-stone-600 border-0">Normal</Badge>;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      eigentuemer: 'Eigentümer',
      reinigung: 'Reinigung',
      reparatur: 'Reparatur'
    };
    return labels[category] || category;
  };

  const hasActiveFilters = filters.status !== 'all' || filters.priority !== 'all' || filters.category !== 'all' || filters.property_id !== 'all' || filters.tag_id !== 'all';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <>
    <div className="space-y-8" data-testid="tasks-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Manrope']">
            Aufgaben
          </h1>
          <p className="text-stone-500 mt-1">{filteredTasks.length} von {tasks.length} Aufgaben</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#0E4C92] hover:bg-[#0A3A70] text-white rounded-full" data-testid="add-task-btn">
              <Plus className="w-4 h-4 mr-2" />
              Neue Aufgabe
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-['Manrope']">Neue Aufgabe erstellen</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="property">Objekt *</Label>
                <Select
                  value={formData.property_id}
                  onValueChange={(value) => setFormData({ ...formData, property_id: value })}
                >
                  <SelectTrigger className="bg-stone-50" data-testid="task-property-select">
                    <SelectValue placeholder="Objekt wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {properties.map((prop) => (
                      <SelectItem key={prop.id} value={prop.id}>
                        {prop.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="title">Titel *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Kurze Beschreibung"
                  required
                  className="bg-stone-50"
                  data-testid="task-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Beschreibung *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Detaillierte Beschreibung..."
                  required
                  className="bg-stone-50 min-h-[100px]"
                  data-testid="task-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category">Kategorie</Label>
                  <Select
                    value={formData.category}
                    onValueChange={handleCategoryChange}
                  >
                    <SelectTrigger className="bg-stone-50" data-testid="task-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="eigentuemer">Eigentümer</SelectItem>
                      <SelectItem value="reinigung">Reinigung</SelectItem>
                      <SelectItem value="reparatur">Reparatur</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="priority">Priorität</Label>
                  <Select
                    value={formData.priority}
                    onValueChange={(value) => setFormData({ ...formData, priority: value })}
                  >
                    <SelectTrigger className="bg-stone-50" data-testid="task-priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="hoch">Hoch</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Text-Generierung aus Tags + Notizen */}
              <div className="space-y-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <Label className="flex items-center gap-2 font-semibold text-[#0E4C92]">
                  <Tag className="w-4 h-4" />
                  Text aus Tags + Notizen generieren
                </Label>
                
                {/* Tag-Auswahl */}
                {categoryTags.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-xs text-slate-600 font-medium">Betroffene Bereiche (Tags):</p>
                    <div className="flex flex-wrap gap-1.5" data-testid="text-generation-tags">
                      {categoryTags.map((tag) => {
                        const isSelected = selectedTextTags.includes(tag.id);
                        return (
                          <button
                            key={tag.id}
                            type="button"
                            onClick={() => handleTextTagToggle(tag.id)}
                            className={`px-2 py-1 rounded-full text-xs font-medium transition-all border ${
                              isSelected 
                                ? 'text-white border-transparent' 
                                : 'bg-white border-slate-300 hover:border-[#0E4C92]'
                            }`}
                            style={{ backgroundColor: isSelected ? tag.color : undefined }}
                            data-testid={`text-tag-${tag.id}`}
                          >
                            {tag.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Notizen (Stichsätze) */}
                <div className="space-y-1.5">
                  <Label htmlFor="raw-notes-new" className="text-xs text-slate-600 font-medium">
                    Notizen (Stichsätze):
                  </Label>
                  <Textarea
                    id="raw-notes-new"
                    value={rawNotes}
                    onChange={(e) => setRawNotes(e.target.value)}
                    placeholder="- Toilettendeckel locker&#10;- Beleuchtung defekt&#10;- Fenster schließt nicht"
                    className="bg-white min-h-[80px] text-xs"
                    data-testid="raw-notes-input"
                  />
                  <p className="text-xs text-slate-500">
                    Eine Zeile pro Punkt. Wird automatisch in vollständige Sätze umgewandelt.
                  </p>
                </div>
                
                <Button
                  type="button"
                  size="sm"
                  onClick={handleGenerateText}
                  disabled={generatingText || !canGenerateText}
                  className="bg-[#0E4C92] hover:bg-[#0A3A70] text-white rounded-lg text-xs w-full"
                  data-testid="generate-text-btn"
                >
                  {generatingText ? (
                    <Loader2 className="w-3 h-3 animate-spin mr-1" />
                  ) : (
                    <Tag className="w-3 h-3 mr-1" />
                  )}
                  Text aus Tags + Notizen generieren
                </Button>
                
                {!canGenerateText && (
                  <p className="text-xs text-amber-600 text-center">
                    Bitte Tags auswählen oder Notizen eingeben
                  </p>
                )}
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="flex-1 rounded-full"
                >
                  Abbrechen
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-[#0E4C92] hover:bg-[#0A3A70] rounded-full"
                  disabled={submitting || properties.length === 0}
                  data-testid="task-submit-btn"
                >
                  {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Erstellen
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search and Filters */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
            <Input
              placeholder="Aufgaben suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white border-stone-200"
              data-testid="task-search"
            />
          </div>
          {hasActiveFilters && (
            <Button
              variant="ghost"
              onClick={clearFilters}
              className="text-stone-500"
              data-testid="clear-filters-btn"
            >
              <X className="w-4 h-4 mr-2" />
              Filter zurücksetzen
            </Button>
          )}
        </div>

        <div className="flex flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-stone-400" />
            <span className="text-sm text-stone-500">Filter:</span>
          </div>
          <Select
            value={filters.status}
            onValueChange={(value) => setFilters({ ...filters, status: value })}
          >
            <SelectTrigger className="w-[150px] bg-white" data-testid="filter-status">
              <SelectValue placeholder="Alle Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Status</SelectItem>
              <SelectItem value="neu">Neu</SelectItem>
              <SelectItem value="in_bearbeitung">In Bearbeitung</SelectItem>
              <SelectItem value="erledigt">Erledigt</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={filters.priority}
            onValueChange={(value) => setFilters({ ...filters, priority: value })}
          >
            <SelectTrigger className="w-[150px] bg-white" data-testid="filter-priority">
              <SelectValue placeholder="Alle Prioritäten" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Prioritäten</SelectItem>
              <SelectItem value="normal">Normal</SelectItem>
              <SelectItem value="hoch">Hoch</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={filters.category}
            onValueChange={(value) => setFilters({ ...filters, category: value })}
          >
            <SelectTrigger className="w-[160px] bg-white" data-testid="filter-category">
              <SelectValue placeholder="Alle Kategorien" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Kategorien</SelectItem>
              <SelectItem value="eigentuemer">Eigentümer</SelectItem>
              <SelectItem value="reinigung">Reinigung</SelectItem>
              <SelectItem value="reparatur">Reparatur</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={filters.property_id}
            onValueChange={(value) => setFilters({ ...filters, property_id: value })}
          >
            <SelectTrigger className="w-[180px] bg-white" data-testid="filter-property">
              <SelectValue placeholder="Alle Objekte" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Objekte</SelectItem>
              {properties.map((prop) => (
                <SelectItem key={prop.id} value={prop.id}>
                  {prop.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {sortedTags.length > 0 && (
            <Select
              value={filters.tag_id}
              onValueChange={(value) => setFilters({ ...filters, tag_id: value })}
            >
              <SelectTrigger className="w-[150px] bg-white" data-testid="filter-tag">
                <SelectValue placeholder="Alle Tags" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Tags</SelectItem>
                {sortedTags.map((tag) => (
                  <SelectItem key={tag.id} value={tag.id}>
                    {tag.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Tasks List */}
      {filteredTasks.length === 0 ? (
        <Card className="bg-white border border-stone-200">
          <CardContent className="py-12 text-center">
            <ClipboardList className="w-12 h-12 text-stone-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">Keine Aufgaben gefunden</h3>
            <p className="text-stone-500 mb-4">
              {searchTerm || hasActiveFilters ? 'Versuchen Sie andere Suchkriterien' : 'Erstellen Sie Ihre erste Aufgabe'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredTasks.map((task) => (
            <Link
              key={task.id}
              to={`/tasks/${task.id}`}
              className="block"
              data-testid={`task-item-${task.id}`}
            >
              <Card className="bg-white border-l-4 border-l-slate-900 border border-stone-200 hover:shadow-md transition-all">
                <CardContent className="p-5">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <h3 className="font-medium text-slate-900">{task.title}</h3>
                        {getPriorityBadge(task.priority)}
                        <Badge variant="outline" className="text-xs">
                          {getCategoryLabel(task.category)}
                        </Badge>
                      </div>
                      <p className="text-sm text-stone-500 line-clamp-2 mb-2">{task.description}</p>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-xs text-stone-400">{task.property_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {getStatusBadge(task.status)}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>

      {/* Confirm Overwrite Dialog */}
      <AlertDialog open={confirmOverwriteOpen} onOpenChange={setConfirmOverwriteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Vorhandenen Text überschreiben?</AlertDialogTitle>
            <AlertDialogDescription>
              Die Beschreibung enthält bereits Text. Möchten Sie diesen mit dem generierten Text überschreiben?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={executeTextGeneration}
              className="bg-[#0E4C92] hover:bg-[#0A3A70] rounded-lg"
              data-testid="confirm-overwrite-btn"
            >
              Überschreiben
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default TasksPage;
